import { formatVND } from "./format";

export type ShareResult = "shared" | "cancelled" | "fallback" | "error";

/**
 * Build the summary message that accompanies the payslip PDF when it is shared
 * to Zalo. Pure function (no browser APIs) so it stays easy to reason about.
 */
export function buildPayslipMessage(
  name: string,
  weekLabel: string,
  totalHours: number,
  totalPay: number,
): string {
  return [
    `Lương tuần ${weekLabel}`,
    name,
    `Tổng giờ: ${totalHours}`,
    `Tổng lương: ${formatVND(totalPay)}`,
  ].join("\n");
}

/** Build a safe filename for a payslip PDF (strips characters not allowed in names). */
export function payslipFileName(name: string, weekId: string): string {
  const safeName = name
    .trim()
    .replace(/[^\p{L}\p{N}]+/gu, "-")
    .replace(/^-+|-+$/g, "");
  return `payslip-${safeName || "nhanvien"}-${weekId}.pdf`;
}

interface SharePayslipOptions {
  url: string;
  fileName: string;
  title: string;
  text: string;
}

/**
 * Share a payslip PDF via the Web Share API so the manager can forward it
 * straight to an employee in Zalo on mobile. Falls back to opening the PDF in a
 * new tab when file sharing is unavailable.
 *
 * Returns a discriminated result so callers can show an appropriate message:
 * - "shared":    handed off to the OS share sheet (Zalo, etc.)
 * - "cancelled": user dismissed the share sheet
 * - "fallback":  file sharing unsupported, opened the PDF in a new tab instead
 * - "error":     could not fetch the PDF, or the share failed unexpectedly
 */
export async function sharePayslipPdf(opts: SharePayslipOptions): Promise<ShareResult> {
  const { url, fileName, title, text } = opts;

  let file: File;
  try {
    const resp = await fetch(url);
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    const blob = await resp.blob();
    file = new File([blob], fileName, { type: "application/pdf" });
  } catch {
    return "error";
  }

  const canShareFiles =
    typeof navigator.share === "function" &&
    typeof navigator.canShare === "function" &&
    navigator.canShare({ files: [file] });

  if (!canShareFiles) {
    window.open(url, "_blank", "noopener");
    return "fallback";
  }

  try {
    await navigator.share({ files: [file], title, text });
    return "shared";
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") {
      return "cancelled";
    }
    return "error";
  }
}
